import React, {
  useCallback,
  useEffect,
  useReducer,
  useState,
  useRef,
  useContext,
  useMemo,
  useImperativeHandle,
  createContext
} from 'react';
import { getAllFields } from '../../../internal-components/form-builder/getAllFields';
import fetchAPI from '../../../utils/fetchAPI';
import generateId from '../../../utils/generateId';
import sortInfoNames from '../../../utils/sortInfoNames';
import AutomationsActionsView from '../view/AutomationsActionsView';
import { AutomationsContex } from '../../engine/FormBuilderAutomationsEngine';
import { AutomationsWizardContext } from './AutomationWizardEngine';
import { TIME_BASED_TRIGGERS } from './AutomationsTriggerEngine';
import SerializationCopy from '../../../utils/SerializationCopy';

export const AutomationActionsContext = createContext({});

export const Actions = {
  STATUS_CHANGE: 'fieldupdate',
  VALUE_CHANGE: 'valueupdate',
  ADMIN: 'assign_to_admin',
  SHARE: 'share',
  ASSIGN_TO_USER: 'assign_to_user',
  EMAIL: 'email',
  WEBHOOK: 'webhook'
};

const defualtActionStructure = {
  [Actions.STATUS_CHANGE]: {
    actionType: 'fieldupdate',
    additionalParameters: { statusData: { status: '' } }
  },
  [Actions.VALUE_CHANGE]: {
    actionType: 'valueupdate',
    additionalParameters: { valueData: { internalId: '', value: '' } }
  },
  [Actions.ADMIN]: { actionType: 'assign_to_admin' },
  [Actions.SHARE]: { actionType: 'share' },
  [Actions.ASSIGN_TO_USER]: { actionType: 'assign_to_user' },
  [Actions.EMAIL]: {
    actionType: 'email',
    additionalParameters: {
      root: {
        emailTemplateId: '',
        openDialog: true
      }
    }
  },
  [Actions.WEBHOOK]: {
    actionType: 'webhook',
    additionalParameters: {
      root: {
        webhookId: ''
      }
    }
  }
};

const ActionsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const addIds = (actions = []) => {
  return actions.map(a => ({ ...a, _id: generateId() }));
};

const removeIds = (actions = []) => {
  return actions.map(a => ({ ...a, _id: undefined }));
};

const cleanActions = (actions = []) => {
  const cleanActions = [];
  actions.forEach(action => {
    const cleanAction = { ...action };
    delete cleanAction._id;

    const doesActionTypeExist = !!cleanAction.actionType;

    const doesStatusExist =
      !!cleanAction.additionalParameters &&
      !!cleanAction.additionalParameters.statusData &&
      !!cleanAction.additionalParameters.statusData.status;

    const doesValueExist =
      !!cleanAction.additionalParameters &&
      !!cleanAction.additionalParameters.valueData &&
      !!cleanAction.additionalParameters.valueData.internalId &&
      !!cleanAction.additionalParameters.valueData.value;

    const doesResponsibleAdminExist =
      !!cleanAction.additionalParameters &&
      !!cleanAction.additionalParameters.root &&
      !!cleanAction.additionalParameters.root.assigneeId;

    const doesShareWithExist =
      !!cleanAction.additionalParameters &&
      !!cleanAction.additionalParameters.root &&
      !!(
        cleanAction.additionalParameters.root.userId ||
        cleanAction.additionalParameters.root.lookupFieldId ||
        cleanAction.additionalParameters.root.userIds ||
        cleanAction.additionalParameters.root.emailFieldId
      );

    const doesSendToExists = !!(
      cleanAction.additionalParameters &&
      cleanAction.additionalParameters.root &&
      cleanAction.additionalParameters.root.to &&
      cleanAction.additionalParameters.root.to.length
    );

    const doesWebhookURLExists = !!(
      cleanAction.additionalParameters &&
      cleanAction.additionalParameters.root &&
      cleanAction.additionalParameters.root.webhookId
    );

    if (
      doesActionTypeExist &&
      (doesStatusExist ||
        doesValueExist ||
        doesResponsibleAdminExist ||
        doesShareWithExist ||
        doesSendToExists ||
        doesWebhookURLExists)
    ) {
      cleanActions.push(cleanAction);
    }
  });
  return cleanActions;
};

const AutomationsActionsEngine = ({
  actions = [],
  updateResults = () => {}
}) => {
  const { form } = useContext(AutomationsContex);
  const { automationData, actionsAPI } = useContext(AutomationsWizardContext);
  const skipRender = useRef(true);
  const [data, dispatch] = useReducer(ActionsReducer, {
    actions: addIds(actions)
  });
  const [underDeleteHover, setUnderDeleteHover] = useState(null);
  const [mailTemplates, setMailTemplates] = useState([]);

  useEffect(() => {
    fetchAPI(
      `/mailer/v1/emails?assetId=${form.id}&assetType=forms&category=forms`
    ).then(response => {
      setMailTemplates(
        response.results.filter(template => !template?.personalMsg)
      );
    });
  }, []);

  useEffect(() => {
    if (skipRender.current) {
      skipRender.current = false;
      return;
    }
    updateResults(cleanActions(data.actions));
  }, [data]);

  const isOptionDisabled = useCallback(
    option => {
      if (option === 'email' || option === 'webhook') {
        return TIME_BASED_TRIGGERS.includes(automationData.rule.when.eventType);
      }
      return !!data.actions.find(action => action.actionType === option);
    },
    [data, automationData]
  );

  const updateAction = useCallback(
    (_id, newValue) => {
      const actionsCopy = JSON.parse(JSON.stringify(data.actions));
      const node2EditIndex = actionsCopy.findIndex(a => a._id === _id);

      const node2EditId = actionsCopy[node2EditIndex]._id;
      actionsCopy[node2EditIndex] = defualtActionStructure[newValue];
      actionsCopy[node2EditIndex]._id = node2EditId;
      dispatch({ type: 'update', payload: { actions: actionsCopy } });
    },
    [data]
  );

  const updateSubAction = useCallback(
    (_id, payload) => {
      const actionsCopy = JSON.parse(JSON.stringify(data.actions));
      const node2EditIndex = actionsCopy.findIndex(a => a._id === _id);

      actionsCopy[node2EditIndex] = {
        ...actionsCopy[node2EditIndex],
        ...payload
      };

      dispatch({ type: 'update', payload: { actions: actionsCopy } });
    },
    [data]
  );

  const addNewAction = useCallback(() => {
    const actionsCopy = JSON.parse(JSON.stringify(data.actions));
    actionsCopy.push({ actionType: 'empty', _id: generateId() });
    dispatch({ type: 'update', payload: { actions: actionsCopy } });
  }, [data]);

  const removeAction = useCallback(
    _id => {
      const actionsCopy = JSON.parse(JSON.stringify(data.actions));
      dispatch({
        type: 'update',
        payload: { actions: actionsCopy.filter(a => a._id !== _id) }
      });
    },
    [data]
  );

  const userLookupFields = useMemo(() => {
    const allFields = getAllFields(form);
    return allFields.filter(field => field.uiType === 'user_lookup');
  }, [form]);

  const emailFields = useMemo(() => {
    const allFields = getAllFields(form);
    return allFields.filter(field => field.uiType === 'email_address');
  }, [form]);

  const getMatchingUsers = useCallback(
    (v = '', currentValues = null) => {
      return fetchAPI(
        `/forms/v1/forms/${form.id}/users?offset=0&limit=100&sort='status' asc&query=[
        [{"field":"'status'","op":"=","value":"'ACTIVE'"}],
        [
          {"field": "'info'->'email'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'lastName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'userName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'firstName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"}
      ]
    ]`.replace(/\n\s+/g, '    ')
      ).then(({ results }) => {
        let assignedUserEmail, mapResults, fullResults;
        assignedUserEmail =
          currentValues && currentValues.info ? currentValues.info.email : '';
        mapResults = results
          .sort(sortInfoNames)
          .filter(u => u.info.email !== assignedUserEmail)
          .map(u => {
            return {
              text: u.info.firstName + ' ' + u.info.lastName,
              value: {
                label: null,
                value: u.id,
                data: {
                  firstName: u.info.firstName,
                  lastName: u.info.lastName,
                  email: u.info.email
                }
              }
            };
          });
        fullResults = [];
        if (currentValues) {
          fullResults = [
            {
              text:
                currentValues.info.firstName +
                ' ' +
                currentValues.info.lastName,
              value: {
                label: null,
                value: currentValues.submitterId,

                data: {
                  firstName: currentValues.info.firstName,
                  lastName: currentValues.info.lastName,
                  email: currentValues.info.email,
                  current: true
                }
              }
            }
          ];
        }
        fullResults = [...fullResults, ...mapResults];
        return fullResults;
      });
    },
    [fetchAPI, form]
  );

  /**
   * Due to https://vinciworksteam.atlassian.net/browse/O20-3564 we need to make sure that email
   * action is disabled if time based trigger is active so we won't be sending multiple emails.
   * This function handles it by updating actionsList, adding disabled propertis (true/false)
   * to email actions.
   */
  const updateActionsListBasedOnTriggerChange = useCallback(
    currentTrigger => {
      const actions = SerializationCopy(data.actions);

      const isEmailActionDisabled =
        TIME_BASED_TRIGGERS.includes(currentTrigger);
      actions.forEach(a => {
        if (a.actionType === 'email' || a.actionType === 'webhook') {
          a.disabled = isEmailActionDisabled;
        }
      });
      dispatch({ type: 'update', payload: { actions } });
    },
    [data]
  );

  useImperativeHandle(
    actionsAPI,
    () => ({
      updateActionsListBasedOnTriggerChange
    }),
    [updateActionsListBasedOnTriggerChange]
  );

  const contextProps = useMemo(() => ({ mailTemplates }), [mailTemplates]);

  const viewProps = {
    actions: data.actions || [],
    isOptionDisabled,
    updateAction,
    updateSubAction,
    addNewAction,
    removeAction,
    underDeleteHover,
    setUnderDeleteHover,
    userLookupFields,
    getMatchingUsers,
    formId: form.id,
    mailTemplates,
    emailFields
  };

  return (
    <AutomationActionsContext.Provider value={contextProps}>
      <AutomationsActionsView {...viewProps} />
    </AutomationActionsContext.Provider>
  );
};

export default AutomationsActionsEngine;
